import { type SiteId } from '@solinkcorp/api-types';
import { getImpersonationURL, locationToRelativePath } from '@solinkcorp/authentication';
import settings from '@solinkcorp/settings';
import { setFilter } from 'actions/filterActions';
import AnalyticsLogger from 'models/AnalyticsLogger';
import AuthService from 'models/AuthService';
import type { NavigateFunction, Location as RouterLocation } from 'react-router-dom';
import type { DispatchType } from 'stores/store';
import * as LocationUtil from 'utils/LocationUtil';
import { type VideoDiscoverySite } from 'utils/types';
import { SETTINGS_URL_PREFIX } from 'utils/urls';
import { SETTINGS_PAGE } from 'vd-constants';

/** Apply the global location filter (timezone follows the selection). */
export function applyLocationFilter(
  dispatch: DispatchType,
  locationsById: Record<string, VideoDiscoverySite> | undefined,
  locationIds: readonly SiteId[],
  locationTags?: readonly string[],
) {
  // Pass through undefined so getLocationsTimezoneByIds can treat "not loaded"
  // differently from an empty map (coercing to {} skips that guard and can throw).
  const timezoneId = LocationUtil.getLocationsTimezoneByIds(locationsById, locationIds);
  dispatch(setFilter({ locationIds, locationTags, timezoneId }));
}

export function goToSettings(navigate: NavigateFunction) {
  navigate(SETTINGS_URL_PREFIX);
  AnalyticsLogger.selectNavigationTab({ tab: SETTINGS_PAGE });
}

export function goToHelpCenter() {
  window.open(settings.helpCenter, '_blank');
  AnalyticsLogger.clickHelpCenter();
}

export function getReferralUrl(): string | undefined {
  const baseUrl = AuthService.getSession()?.clientSettings?.referralUrl;
  const profile = AuthService.getSession()?.profile;
  if (!baseUrl || !profile) {
    return undefined;
  }
  const { email, name } = profile;

  const namesArray = (name || '').split(' ');
  const lastname = namesArray.splice(namesArray.length - 1, 1)[0] || '';
  const firstname = namesArray.join(' ');

  const params = new URLSearchParams({
    email,
    lastname,
    firstname,
  });

  const safeUrl = baseUrl.replace('http:', 'https:');
  return `${safeUrl}?${params.toString()}`;
}

export function goToReferral() {
  const referralUrl = getReferralUrl();
  if (!referralUrl) return;
  window.open(referralUrl, '_blank');
  AnalyticsLogger.clickReferral();
}

export function goToCallhomeUser() {
  const profile = AuthService.getSession()?.profile;
  if (!profile) return;

  const { id, customerId } = profile;
  const url = `${settings.manage_url}/customer/${customerId}/users/${id}/settings`;
  window.open(url, '_blank');
}

export function switchUsers(navigate: NavigateFunction, location: RouterLocation) {
  const redirectTo = locationToRelativePath(location);
  navigate(getImpersonationURL(redirectTo));
}

export function logout(navigate: NavigateFunction) {
  navigate('/logout');
}
